import {useCallback, useContext, useEffect, useRef} from 'react';
import {AppCtx} from "../../../AppCtx";
import {fetchFileContent} from "../../song/services/songs-db";
import {useApplicationEvents, useEventListener} from "../../../infrastructure/events/useBroadcastChannel";
import {CLIENT_PROCESS_ID, FileBrowserFileSelectedEvent, 
  UrlSongChangedChannel, UrlSongChangedEvent} from "../../../infrastructure/events/channels";
import {updateRecentFiles} from "../../../infrastructure/storage/localStorage";
import {useNavigate, useLocation} from 'react-router-dom';
import {songPathFromUrl, songPathUrl} from '../../../shared/services/songPathUtils';

const SONG_ROUTE_PREFIX = /^\/songs\/?(.*)$/;
const SONG_NUMBER_PREFIX = '#song-title-';

const parsePathAndSong = (pathname = '', hash = '') => {
  const match = pathname.match(SONG_ROUTE_PREFIX);
  const rawFilePath = match?.[1] ?? '';
  const filePath = rawFilePath ? songPathFromUrl(pathname) : undefined;
  const songNumber = hash?.startsWith(SONG_NUMBER_PREFIX)
    ? hash.slice(SONG_NUMBER_PREFIX.length)
    : undefined;

  return {filePath, songNumber};
};

export const useSelectSong = () => {
  const {setSearchQuery, setSelectedFile, setSearchResults, isCommonSessionEnabled} = useContext(AppCtx);
  const {postMessage: postFileBrowserFileSelected} = useApplicationEvents(FileBrowserFileSelectedEvent);
  const navigate = useNavigate();

  const selectSongPvt = useCallback((filePath, songNumber, produceEvent, pushState, alreadySelected, notifyOthers, replace = false) => {
    if (!filePath) return console.error('File path is undefined');

    setSearchResults([]);
    setSearchQuery('');

    const targetId = songNumber ? `song-title-${songNumber}` : undefined;
    pushState && navigate(`${songPathUrl(filePath)}${targetId?`#${targetId}`:''}`, {replace});
    produceEvent && isCommonSessionEnabled && postFileBrowserFileSelected({fileName:filePath, songNumber, notifyOthers});

    if (alreadySelected) {
      updateRecentFiles({fileName:filePath, songNumber});
    } else {
      fetchFileContent(filePath).then(content => {
        updateRecentFiles({fileName:filePath, songNumber});
        setSelectedFile({name:filePath, content, found:content!==null, songNumber });
        targetId && setTimeout(()=>document.getElementById(targetId)?.scrollIntoView(), 300);
      });
    }
  }, [isCommonSessionEnabled, navigate, postFileBrowserFileSelected, setSearchQuery, setSearchResults, setSelectedFile]);

  useEventListener(
    UrlSongChangedEvent,
    useCallback(({filePath, songNumber = ''} = {}) => {
      if (!isCommonSessionEnabled) return;
      if (filePath && /\.txt$/i.test(filePath)) {
        selectSongPvt(filePath, songNumber, false, false);
      }
    }, [isCommonSessionEnabled, selectSongPvt]),
    [],
    {filterSameProcess: true}
  );

  useEventListener(
    FileBrowserFileSelectedEvent,
    useCallback(evt => {
      if (!isCommonSessionEnabled) return;
      if (!evt?.fileName) return;

      const fromOtherProcess = evt.__processId && evt.__processId !== CLIENT_PROCESS_ID;
      const fromPresenterView = evt.__source === 'presenter-view';
      const fromWs = evt.__source === 'ws';

      if ((fromWs && fromOtherProcess) || fromPresenterView) {
        selectSongPvt(evt.fileName, evt.songNumber, false, true, evt.alreadySelected, evt.notifyOthers, true);
      }
    }, [isCommonSessionEnabled, selectSongPvt])
  );

  return useCallback((filePath, songNumber, alreadySelected, notifyOthers, options = {}) => {
    const {produceEvent = true, pushState = true, replace = false} = options;
    selectSongPvt(filePath, songNumber, produceEvent, pushState, alreadySelected, notifyOthers, replace);
  }, [selectSongPvt]);
};

const urlSongChanged = new UrlSongChangedChannel();

export const SongRouteHandler = () => {
  const location = useLocation();
  const {setSearchResults, isCommonSessionEnabled} = useContext(AppCtx);
  const selectSong = useSelectSong();
  const lastHandledRoute = useRef(null);

  useEffect(() => {
    const route = `${location.pathname}${location.hash}`;
    if (lastHandledRoute.current === route) return;
    lastHandledRoute.current = route;
    if (location.pathname.startsWith('/songs/')) {
      setSearchResults([]);
      const {filePath, songNumber} = parsePathAndSong(location.pathname, location.hash);
      if (filePath && /\.txt$/i.test(filePath)) {
        selectSong(filePath, songNumber, undefined, undefined, {produceEvent:false, pushState:false});
        if (isCommonSessionEnabled) urlSongChanged.postMessage({filePath, songNumber});
      }
    }
  }, [isCommonSessionEnabled, location.pathname, location.hash, selectSong, setSearchResults]);

  return null;
};
